import React, { useEffect, useRef, useState } from "react";
import FormikInput from "../../../../Components/Formik/FormikInput";
import { useLanguage } from "../../../../Components/LangTranslation/LanguageContext";
import { fileToBase64 } from "../../../../utilities/fileToBase64";
import { useFormikContext } from "formik";
import ColorThief from "colorthief";
import { DEFAULT_PALETTES } from "../../../../utilities/constants";

function ShopInformationStep() {
  const { language, translations } = useLanguage();
  const t = translations[language];
  const { setFieldValue, setFieldTouched, errors, touched, values , setFieldError  } =
    useFormikContext();

  // File upload states
  const fileInputRefHero = useRef();
  const [fileNameHero, setFileNameHero] = useState("");
  const fileInputRefLogo = useRef();
  const [fileNameLogo, setFileNameLogo] = useState("");
  const [heroImagePreview, setHeroImagePreview] = useState(null);
  const [logoImagePreview, setLogoImagePreview] = useState(null);

  // Color extraction state
  const [colors, setColors] = useState([]);
  const [colorsLogo, setColorsLogo] = useState([]);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const imageRef = useRef(null);
  // console.log("colors", colors, colorsLogo);
  // console.log("DEFAULT_PALETTES", DEFAULT_PALETTES);
  // Convert RGB to HEX
  const rgbToHex = (r, g, b) =>
    "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");

  const handleImageUpload = (e, setFile, key, setImagePreview, isLogo) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (2MB = 2 * 1024 * 1024 bytes)
    if (file.size > 2 * 1024 * 1024) {
      setFieldError(key, "Image size should not exceed 2MB!");
      setFieldTouched(key, true, false);
      return;
    }

    setFieldError(key, "");
    setFieldTouched(key, false);
    setFile(file.name);

    fileToBase64(file, (_, base64) => {
      setFieldValue(key, base64 || "");
    });

    const reader = new FileReader();
    reader.onload = (event) => {
      setImagePreview(event.target.result);
      if (isLogo) {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          imageRef.current = img;
          extractColors(); // Only for logo
        };
      }
    };
    reader.readAsDataURL(file);
  };
  

  // Extract colors using ColorThief
  const extractColors = () => {
    if (!imageRef.current) return;

    const colorThief = new ColorThief();
    try {
      const dominantColor = colorThief.getColor(imageRef.current); // Dominant color for the logo
      const palette = colorThief.getPalette(imageRef.current, 4); // Extract palette for the logo

      // Normalize and remove duplicates
      const uniqueColors = [
        dominantColor,
        ...palette.filter((color) => color.length === 3), // Filter out invalid color data
      ].map((color, index) => ({
        // rgb: `rgb(${color[0]}, ${color[1]}, ${color[2]})`,
        hex: rgbToHex(color[0], color[1], color[2]), // Convert RGB to HEX
        status: index === 0 ? "primary" : index === 1 ? "secondary" : null,
      }));

      setColors(uniqueColors); // Update the colors state
      setColorsLogo(uniqueColors);
    } catch (error) {
      console.error("Color extraction failed:", error); // Log errors if extraction fails
    }
  };
  // Handle custom color changes
  const handleCustomColorChange = (index, color) => {
    setColors((prevColors) => {
      const updatedColors = [...prevColors];
      updatedColors[index] = { ...updatedColors[index], hex: color }; // Preserve object structure
      return updatedColors;
    });
  };
  const handlePaletteSelect = (selectedColors) => {
    setColors(
      selectedColors.map((color) => ({ hex: color })) // Map the hex values to the desired structure
    );
    setIsPaletteOpen(false);
  };
  // Function to reset colors to the values in colorsLogo
  const resetPalette = () => {
    setColors([...colorsLogo]); // Replace colors with colorsLogo
  };

  // Upload button component
  const UploadFileButton = ({
    inputRef,
    fileName,
    uploadText,
    setState,
    keyName,
    setImagePreview,
    isLogo,
  }) => (
    <div className="d-grid mb-2">
      <div
        style={{
          border: "1px solid #cbcbcb",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        role="button"
        onClick={() => inputRef.current.click()}
        className="p-2 px-3 d-flex justify-content-center align-items-center"
      >
        <i className="fa-regular fa-image text-danger me-3 fa-lg"></i>
        <span className="text-truncate" style={{ minWidth: 0 }}>
          {t[uploadText] + "*"} {fileName}
        </span>
      </div>
      <input
        ref={inputRef}
        onChange={(e) =>
          handleImageUpload(e, setState, keyName, setImagePreview, isLogo)
        }
        type="file"
        name={keyName}
        hidden
        accept="image/*"
      />
      {touched[keyName] && errors[keyName] && (
        <span className="ps-2 error-message">{errors[keyName]}</span>
      )}
    </div>
  );

  const handleColorClick = (index) => {
    if (colors.length === 0) return;
    let updatedColors = [...colors];
    if (!updatedColors[index].hasOwnProperty("status")) {
      updatedColors[index].status = null;
    }
    const primaryIndex = updatedColors.findIndex((c) => c.status === "primary");
    const secondaryIndex = updatedColors.findIndex(
      (c) => c.status === "secondary"
    );
    if (updatedColors[index].status) {
      updatedColors[index].status = null;
      if (index === primaryIndex && secondaryIndex !== -1) {
        updatedColors[secondaryIndex].status = "primary";
      }
    } else {
      if (primaryIndex === -1) {
        updatedColors[index].status = "primary";
      } else if (secondaryIndex === -1) {
        updatedColors[index].status = "secondary";
      }
    }

    setColors(updatedColors);
  };

  useEffect(() => {
    if (!values.website_logo) return;
    if (colors.length === 0) {
      setFieldTouched("website_palette", true);
    }
    setFieldValue("website_palette", colors);
  }, [colors, setFieldValue, setFieldTouched, values.website_logo]);

  return (
    <>
      <div
        style={{
          border: "1px solid #cbcbcb",
          borderRadius: "15px",
          borderStyle: "dashed",
        }}
        className="col-12 row me-0 pe-0"
      >
        <div className="col-12 mt-3">
          <h6 className="text-center text-secondary fw-bold">
            {t["Upload images for Logo & Hero Section"]}
          </h6>
        </div>

        <div className="d-flex flex-lg-row flex-column my-3 col-12 p-4 justify-content-center align-items-start gap-3">
          <div className="d-flex flex-column justify-content-center align-items-center w-100">
            <UploadFileButton
              fileName={fileNameLogo}
              inputRef={fileInputRefLogo}
              keyName="website_logo"
              setState={setFileNameLogo}
              uploadText="Logo Image"
              setImagePreview={setLogoImagePreview}
              isLogo={true}
            />
            {logoImagePreview && (
              <img
                src={logoImagePreview}
                alt="Logo Preview"
                style={{
                  maxWidth: "100px",
                  maxHeight: "100px",
                  objectFit: "cover",
                }}
              />
            )}
          </div>
          <div className="d-flex flex-column justify-content-center align-items-center w-100">
            <UploadFileButton
              fileName={fileNameHero}
              inputRef={fileInputRefHero}
              keyName="website_hero_img"
              setState={setFileNameHero}
              uploadText="Hero Image"
              setImagePreview={setHeroImagePreview}
              isLogo={false}
            />
            {heroImagePreview && (
              <img
                src={heroImagePreview}
                alt="Hero Preview"
                style={{
                  maxWidth: "100px",
                  maxHeight: "100px",
                  objectFit: "cover",
                }}
              />
            )}
          </div>
        </div>
      </div>
      <div className="my-3 col-12">
        <div
          name="website_palette"
          className="palettes-box d-flex flex-column flex-md-row justify-content-between align-items-center"
          style={{
            position: "relative",
            pointerEvents: colors.length > 0 ? "auto" : "none",
            cursor: "pointer",
            opacity: colors.length > 0 ? 1 : 0.5,
          }}
          onClick={() => {
            if (colors.length > 0) setIsPaletteOpen((prev) => !prev);
          }}
        >
          <span
            className={`label-palettes-box ${
              colors.length === 0 ? "disabled" : "cursor-pointer"
            }`}
          >
            {t["Brand Color Palette"]}
          </span>
          {colors?.length > 0 && (
            <div
              className="palette-wrapper me-3"
              onClick={(e) => e.stopPropagation()}
            >
              {colors.map((color, index) => {
                const primaryIndex = colors.findIndex(
                  (c) => c.status === "primary"
                );
                const secondaryIndex = colors.findIndex(
                  (c) => c.status === "secondary"
                );

                // Disable only when both primary & secondary are set
                const isDisabled =
                  primaryIndex !== -1 && secondaryIndex !== -1 && !color.status;

                return (
                  <div key={index} className="color-container text-center">
                    <div
                      className={`color-box ${color.status} ${
                        isDisabled ? "disabled" : ""
                      }`}
                      style={{
                        backgroundColor: color.hex,
                        border: color.status
                          ? "3px solid #b3b3b3"
                          : "1px solid #b3b3b3",
                        opacity: isDisabled ? 0.4 : 1,
                        pointerEvents: isDisabled ? "none" : "auto",
                      }}
                      onClick={() => handleColorClick(index)}
                    ></div>
                    {color.status === "primary" && (
                      <i
                        className="fa-solid fa-p"
                        style={{ fontSize: "0.75rem", color: "#FF5837" }}
                      ></i>
                    )}
                    {color.status === "secondary" && (
                      <i
                        className="fa-solid fa-s"
                        style={{
                          fontSize: "0.75rem",
                          color: "#000",
                        }}
                      ></i>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <span
            onClick={() => {
              if (colors.length > 0) setIsPaletteOpen((prev) => !prev);
            }}
            className={`palettes-box-toggler fa-solid ${
              isPaletteOpen ? "fa-caret-up" : "fa-caret-down"
            } ${colors.length === 0 ? "disabled" : "cursor-pointer"}`}
          ></span>
          <div
            className={`palette-dropdown  ${
              isPaletteOpen ? "palette-dropdown-show" : ""
            }`}
          >
            <div className="d-flex flex-column">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-center me-3 my-3">
                <button
                  className="badge rounded-pill bg-dark"
                  onClick={() => resetPalette()}
                >
                  <span className="fst-italic">
                    {t["Reset to Logo Palette"]}
                  </span>
                  <i className="fa-solid fa-arrows-rotate cursor-pointer me-1"></i>
                </button>
                <div className="d-flex flex-column justify-content-center alignitems-center">
                  <span className="label-palettes-box fst-italic fw-bold text-muted">
                    <i className="fa-solid fa-palette palette-gradient"></i>
                    {t["ustomize"]}
                  </span>
                  <div className="palette-wrapper me-3">
                    {colors?.map((color, index) => (
                      <input
                        key={index}
                        type="color"
                        value={color.hex}
                        onChange={(e) =>
                          handleCustomColorChange(index, e.target.value)
                        }
                        style={{
                          width: "18px",
                          height: "27px",
                          border: "1px solid #000",
                          borderRadius: "4px",
                          padding: "0px",
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <h6 className="text-center text-muted fw-bold mb-0">
                {t["Default Palettes"]}
              </h6>
              <div className="row justify-content-center">
                {DEFAULT_PALETTES?.map((palette, index) => (
                  <div
                    key={index}
                    className="palette-wrapper me-3 my-3"
                    onClick={() => handlePaletteSelect(palette.hex)} // On click, update colors state with this palette's hex values
                  >
                    {palette.hex.map((color, colorIndex) => (
                      <div
                        key={colorIndex}
                        style={{
                          width: "18px",
                          height: "27px",
                          backgroundColor: color,
                          border: "1px solid #000",
                          borderRadius: "4px",
                          opacity: "0.7", // Adjust opacity to show colors
                        }}
                      ></div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {touched["website_palette"] && errors["website_palette"] && (
          <span className="ps-2 error-message">
            {errors["website_palette"]}
          </span>
        )}
      </div>
      <div className="my-3 col-md-6 col-12">
        <FormikInput name="website_name" placeholder={t["Website Name"]} />
      </div>

      <div className="my-3 col-md-6 col-12">
        <FormikInput name="domain" disabled={true} placeholder={t["Domain"]} />
      </div>
    </>
  );
}

export default ShopInformationStep;
